import { FinanceType, Condition } from '@upstart/harmony/lib/legos/constants';
import React from 'react';
import { Button } from 'react-bootstrap';
// @ts-ignore
import { Confirm } from 'react-confirm-bootstrap';
import classNames from 'classnames';
import ProdigyInput from '../../../GlobalUI/ProdigyInput';
import '../style.scss';
import { Tier } from '../types';
import {
  AprConfigValidationResult,
  getValidationKey,
} from '../useAprConfigValidator';
import {
  TIERS_MIN_LENGTH,
  ORDERED_TIERS,
  TIERS_MAX_LENGTH,
} from '../constants';
import { AmendendedFinanceType } from '../useAprConfig';

interface TierDefinitionProps {
  condition: Condition;
  purchase: Tier[];
  lease: Tier[];
  validationResult: AprConfigValidationResult;
  onTiersDefinitionsUpdate: (
    financeType: AmendendedFinanceType,
    index: number,
    definition: Partial<Tier>,
  ) => void;
  onTiersDefinitionCopy: (
    origin: AmendendedFinanceType,
    target: AmendendedFinanceType,
  ) => void;
  onTiersDefinitionAdd: (financeType: AmendendedFinanceType) => void;
  onTiersDefinitionRemove: (
    financeType: AmendendedFinanceType,
    removeIndex: number,
  ) => void;
}

function TiersDefinition({
  condition,
  purchase,
  lease,
  validationResult,
  onTiersDefinitionsUpdate,
  onTiersDefinitionCopy,
  onTiersDefinitionAdd,
  onTiersDefinitionRemove,
}: TierDefinitionProps) {
  const renderTierDetail = (
    index: number,
    tier: Tier,
    financeType: AmendendedFinanceType,
  ) => {
    const hideRemoveButton =
      (financeType === FinanceType.LEASE ? lease.length : purchase.length) <=
      TIERS_MIN_LENGTH;
    const lowError = getValidationKey({
      financeType,
      tierIndex: index,
      suffix: 'low',
    });
    const highError = getValidationKey({
      financeType,
      tierIndex: index,
      suffix: 'high',
    });
    const displayName = getValidationKey({
      financeType,
      tierIndex: index,
      suffix: 'displayName',
    });
    const label = `Tier ${index + 1}`;
    return (
      <div
        key={`tier-definition-${ORDERED_TIERS[index]}`}
        className="flex flex-row no-flex-wrap space-x-15 items-start justify-center"
      >
        <div className="flex h-full mt-25">
          <h4 className="whitespace-nowrap h-full">{label}</h4>
        </div>
        <div className="flex flex-col w-200 h-full">
          <label style={{ height: '20px' }}>{index === 0 && 'Tier Name'}</label>

          <div className="h-full">
            <ProdigyInput
              value={tier.displayName}
              className={
                (validationResult?.errors?.[displayName]
                  ? 'tier-error'
                  : '') as string
              }
              onChangeHandler={(e: string) => {
                onTiersDefinitionsUpdate(financeType, index, {
                  displayName: e,
                });
              }}
            />
            {validationResult?.errors?.[displayName] && (
              <ul className="error-list">
                <li>{validationResult?.errors?.[displayName]}</li>
              </ul>
            )}
          </div>
        </div>
        <div className="flex flex-col w-200 h-full">
          <label>Low Range</label>
          <div className="h-full">
            <ProdigyInput
              value={tier.low}
              className={
                (validationResult?.errors?.[lowError]
                  ? 'tier-error'
                  : '') as never
              }
              onChangeHandler={(e: string) => {
                const value = e.replace(/\D/, '');
                onTiersDefinitionsUpdate(financeType, index, { low: value });
              }}
            />
            {validationResult?.errors?.[lowError] && (
              <ul className="error-list">
                {validationResult?.errors?.[lowError].map((l) => (
                  <li>{l}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex flex-col w-200 h-full">
          <label>High Range</label>
          <div>
            <ProdigyInput
              value={tier.high}
              className={
                (validationResult?.errors?.[highError]
                  ? 'tier-error'
                  : '') as never
              }
              onChangeHandler={(e: string) => {
                const value = e.replace(/\D/, '');
                onTiersDefinitionsUpdate(financeType, index, { high: value });
              }}
            />
            {validationResult?.errors?.[highError] && (
              <ul className="error-list">
                {validationResult?.errors?.[highError].map((h) => (
                  <li>{h}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="mt-25">
          {hideRemoveButton ? (
            <Button className={classNames('close-button-confirm', 'disabled')}>
              x
            </Button>
          ) : (
            <Confirm
              onConfirm={() =>
                !hideRemoveButton && onTiersDefinitionRemove(financeType, index)
              }
              body={`Are you sure you want delete? This will DELETE all ${label} APR settings on this page.`}
              confirmText="Remove"
              title={`Remove ${label}`}
            >
              <Button className={classNames('close-button-confirm')}>x</Button>
            </Confirm>
          )}
        </div>
        <div className="mt-30 is-default-radio-button flex">
          <input
            type="radio"
            onClick={() =>
              onTiersDefinitionsUpdate(financeType, index, { isDefault: true })
            }
            checked={tier.isDefault}
            className="is-default-radio-button-input"
          />
          <label className="is-default-radio-button-label">Default</label>
        </div>
      </div>
    );
  };

  const addFinanceTierButton = (
    buttonText: string,
    disabled: boolean,
    onClick: () => void,
    isPrimary = true,
  ) => (
    <Button
      bsClass={`btn ${isPrimary ? 'btn-primary' : ''} btn-large custom-button`}
      disabled={disabled}
      onClick={onClick}
    >
      {buttonText}
    </Button>
  );
  return (
    <>
      <legend>Credit Tiers</legend>
      <div className="flex flex-row space-x-30 items-start justify-center">
        <div className="flex flex-col space-y-15">
          <h4>Finance Tiers</h4>
          {purchase.map((tier, idx) =>
            renderTierDetail(idx, tier, FinanceType.PURCHASE),
          )}
          <div className="flex flex-row space-x-10 h-100">
            {addFinanceTierButton(
              'Add Finance Credit Tier',
              purchase.length === TIERS_MAX_LENGTH,
              () => onTiersDefinitionAdd(FinanceType.PURCHASE),
              true,
            )}
          </div>
        </div>
        {condition === Condition.NEW && (
          <div className="flex flex-col space-y-15 lease-tiers">
            <h4>Lease Tiers</h4>
            {lease.map((tier, idx) =>
              renderTierDetail(idx, tier, FinanceType.LEASE),
            )}
            <div className="flex flex-row space-x-10 h-100">
              {addFinanceTierButton(
                'Add Lease Credit Tier',
                lease.length === TIERS_MAX_LENGTH,
                () => onTiersDefinitionAdd(FinanceType.LEASE),
                true,
              )}
              {addFinanceTierButton(
                'Copy Tiers From Finance',
                false,
                () =>
                  onTiersDefinitionCopy(
                    FinanceType.PURCHASE,
                    FinanceType.LEASE,
                  ),
                false,
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default TiersDefinition;
